import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { DemanderLigne } from '../kit/DemanderLigne';
import { createPortal } from 'react-dom';
import { X, AlertTriangle } from 'lucide-react';
import '../../styles/ig-kit.css';

/* Vrai quand on est rendu dans une IgSheet (les listes d'actions passent alors « à plat », une ligne par action). */
export const IgInSheet = createContext(false);
export const useInSheet = () => useContext(IgInSheet);

// LOT 22 · plus rien ne dépasse : dans une feuille, un libellé coupé par « … » passe à la ligne ; un montant (chiffres + devise) garde sa ligne
const MONTANT = /^[\d\s\u00a0\u202f.,%+−\-:/·×]*[\d][\d\s\u00a0\u202f.,%+−\-:/·×]*(\s?(MAD|EUR|€|HT|TTC|TVA|j|h|kg|g|L|cl|★))*$/;
const deplier = (root) => {
  if (!root) return;
  root.querySelectorAll('*').forEach((e) => {
    if (e.dataset.plie || e.closest('.sel53')) return;
    const cs = getComputedStyle(e); const t = (e.innerText || '').trim();
    if (e.children.length) {
      if (cs.webkitLineClamp && cs.webkitLineClamp !== 'none' && e.scrollHeight > e.clientHeight + 1) { e.style.setProperty('-webkit-line-clamp', 'unset', 'important'); e.style.setProperty('display', 'block', 'important'); e.style.setProperty('max-height', 'none', 'important'); e.style.setProperty('overflow', 'visible', 'important'); e.dataset.plie = '1'; }
      return;
    }
    if (cs.display === 'inline' && /\d/.test(t) && t.length <= 24 && e.getClientRects().length > 1) { e.style.whiteSpace = 'nowrap'; e.dataset.plie = '1'; return; }
    const clamp = cs.webkitLineClamp && cs.webkitLineClamp !== 'none' && e.scrollHeight > e.clientHeight + 1;
    if (!clamp && (cs.textOverflow !== 'ellipsis' || e.scrollWidth <= e.clientWidth + 1)) return;
    if (MONTANT.test(t)) return;
    if (clamp) { e.style.setProperty('-webkit-line-clamp', 'unset', 'important'); e.style.setProperty('display', 'block', 'important'); e.style.setProperty('max-height', 'none', 'important'); }
    e.style.setProperty('white-space', 'normal', 'important'); e.style.setProperty('text-overflow', 'clip', 'important');
    e.style.setProperty('overflow', 'visible', 'important'); e.style.setProperty('overflow-wrap', 'anywhere'); e.dataset.plie = '1';
  });
};

/* Feuille « mode Instagram » : glisse du bas sur téléphone, fenêtre centrée sur ordinateur. Une seule enveloppe pour toute l'appli. */
const PILE = [];  // LOT 53 · Échap ne ferme que la feuille du dessus

export const IgSheet = ({ open = true, onClose, title, kicker, wide, half, children, foot, testId = 'ig-sheet', z = 120, className = '', sansDemander }) => {
  const ref = useRef(null);
  const fermer = useRef(onClose);
  fermer.current = onClose;
  useEffect(() => {
    if (!open) return undefined;
    const moi = {};
    PILE.push(moi);
    const k = (e) => { if (e.key === 'Escape' && !e.defaultPrevented && PILE[PILE.length - 1] === moi) { e.preventDefault(); fermer.current?.(); } };
    document.addEventListener('keydown', k);
    document.body.classList.add('ig-sheet-open');
    return () => { document.removeEventListener('keydown', k); PILE.splice(PILE.indexOf(moi), 1); if (!PILE.length) document.body.classList.remove('ig-sheet-open'); };
  }, [open]);
  useEffect(() => {
    if (!open || !ref.current) return undefined;
    let t = null; const run = () => { clearTimeout(t); t = setTimeout(() => deplier(ref.current), 120); };
    run(); const mo = new MutationObserver(run); mo.observe(ref.current, { childList: true, subtree: true, characterData: true });
    return () => { mo.disconnect(); clearTimeout(t); };
  }, [open]);
  // LOT 61 · R2 · clavier ouvert : la feuille passe en plein écran, à la hauteur visible, et ne se replie jamais
  const [plein, setPlein] = useState(false);
  useEffect(() => {
    if (!open || !ref.current) return undefined;
    const el = ref.current; const vv = window.visualViewport;
    const taille = () => { if (vv) el.style.setProperty('--vvh', `${Math.round(vv.height)}px`); };
    const focus = (e) => { if (window.innerWidth < 768 && e.target.matches?.('input:not([type=checkbox]):not([type=radio]), textarea, select, [contenteditable=true]')) setPlein(true); };
    taille(); vv?.addEventListener('resize', taille); el.addEventListener('focusin', focus);
    return () => { vv?.removeEventListener('resize', taille); el.removeEventListener('focusin', focus); };
  }, [open]);
  // LOT 62 · T4 · la poignée suit le doigt : vers le haut = plein écran, vers le bas = mi-hauteur puis fermer
  const geste = useRef(null);
  const tirer = {
    onPointerDown: (e) => { if ((e.pointerType === 'mouse' && e.button !== 0) || e.target.closest?.('button, a, input')) return; geste.current = { y: e.clientY, t: Date.now() }; e.currentTarget.setPointerCapture?.(e.pointerId); },
    onPointerMove: (e) => { const g = geste.current; if (!g || !ref.current) return; const dy = e.clientY - g.y; ref.current.style.transition = 'none'; ref.current.style.transform = dy > 0 ? `translateY(${dy}px)` : `translateY(${dy / 4}px)`; },
    onPointerUp: (e) => { const g = geste.current; geste.current = null; if (!g || !ref.current) return; const dy = e.clientY - g.y; const v = dy / Math.max(1, Date.now() - g.t); ref.current.style.transition = 'transform .22s cubic-bezier(.2,.8,.2,1)'; ref.current.style.transform = '';
      if (dy > 110 || v > 0.6) { if (plein) setPlein(false); else fermer.current?.(); } else if (dy < -50 || v < -0.5) setPlein(true); },
    onPointerCancel: () => { geste.current = null; if (ref.current) { ref.current.style.transform = ''; } },
  };
  if (!open) return null;
  return createPortal(
    <IgInSheet.Provider value>
    <div className={`ig-overlay ${className}`} style={{ zIndex: z }} onClick={onClose} data-testid={`${testId}-overlay`}>
      <div className={`ig-sheet ${wide ? 'is-wide' : ''} ${half ? 'is-half' : ''} ${plein ? 'is-plein' : ''} ${/reglages|outils/.test(testId) ? 'ig28-reglages' : ''}`} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} data-testid={testId} ref={ref}>
        <div className="ig-grip" aria-hidden {...tirer} data-testid={`${testId}-poignee`} />
        {(title || onClose) && (
          <div className="ig-sheet-head" {...tirer}>
            <div className="min-w-0">
              {kicker && <p className="ig-sheet-kicker">{kicker}</p>}
              {title && <h3 className="ig-sheet-title" data-testid={`${testId}-titre`}>{title}</h3>}
            </div>
            {onClose && <button type="button" className="ig-sheet-x" onClick={onClose} title="Fermer" data-testid={`${testId}-fermer`}><X size={18} /></button>}
          </div>
        )}
        <div className="ig-sheet-body">{children}{!sansDemander && !/conversation|confirm/.test(testId) && <DemanderLigne sheetTestId={testId} refSheet={ref} />}</div>
        {foot && <div className="ig-sheet-foot">{foot}</div>}
      </div>
    </div>
    </IgInSheet.Provider>,
    document.body,
  );
};

/* Confirmation épurée : Annuler / Confirmer (rouge si destructif). API impérative : `await confirmer('Supprimer ?')`. */
let _ask = null;
export const confirmer = (message, opts = {}) => {
  if (!_ask) return Promise.resolve(window.confirm(typeof message === 'string' ? message : opts.title || ''));
  return _ask(typeof message === 'string' ? { message, ...opts } : { ...message, ...opts });
};

const destructif = (txt = '') => /supprim|effac|retir|vider|annuler l|refus|résilier|archiv|définitiv|irréversible|révoquer|déconnect/i.test(txt);

export const IgConfirmProvider = ({ children }) => {
  const [q, setQ] = useState(null);
  useEffect(() => {
    _ask = (o) => new Promise((resolve) => setQ({ ...o, resolve }));
    return () => { _ask = null; };
  }, []);
  const done = (v) => { q?.resolve(v); setQ(null); };
  const lines = String(q?.message || '').split('\n').filter(Boolean);
  const danger = q ? (q.danger ?? destructif(`${q.title || ''} ${q.message || ''} ${q.okLabel || ''}`)) : false;
  return (
    <>
      {children}
      {q && (
        <IgSheet half onClose={() => done(false)} testId="ig-confirm" z={1400}
          foot={<>
            <button type="button" className="btn-kit btn-kit-light" onClick={() => done(false)} data-testid="ig-confirm-annuler">{q.cancelLabel || 'Annuler'}</button>
            <button type="button" className={`btn-kit ${danger ? 'btn-kit-danger' : 'btn-kit-dark'}`} onClick={() => done(true)} data-testid="ig-confirm-ok">{q.okLabel || (danger ? 'Confirmer' : 'Oui, continuer')}</button>
          </>}>
          <div className="ig-confirm">
            <span className={`ig-confirm-ico ${danger ? 'is-danger' : ''}`} aria-hidden><AlertTriangle size={22} /></span>
            <div className="min-w-0">
              <p className="ig-confirm-title" data-testid="ig-confirm-titre">{q.title || lines[0] || 'Confirmer ?'}</p>
              {(q.title ? lines : lines.slice(1)).map((l, i) => <p key={i} className="ig-confirm-text">{l}</p>)}
            </div>
          </div>
        </IgSheet>
      )}
    </>
  );
};

/* État vide : une phrase + un bouton (règle mobile 12). */
export const IgVide = ({ Icon, titre, texte, action, testId = 'ig-vide' }) => (
  <div className="ig-vide" data-testid={testId}>
    {Icon && <span className="ig-empty-ring"><Icon size={26} data-keepicon /></span>}
    {titre && <p className="ig-vide-titre">{titre}</p>}
    {texte && <p className="ig-vide-texte">{texte}</p>}
    {action && <button type="button" className="btn-kit btn-kit-dark" onClick={action.onClick} data-testid={`${testId}-action`}>{action.label}</button>}
  </div>
);
